import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';
import { environment } from '@orderna/admin-panel/src/environments/environment';
import { Maybe } from '@orderna/admin-panel/src/types/maybe';
import { QueryOptions } from '@orderna/admin-panel/src/types/query-options';
import { Data } from '@orderna/admin-panel/src/utils/service';
import { IStoresApiService } from './stores-api.interface';
import { ICategory } from '../../model/category';
import { IProduct } from '../../model/product';
import { IStore, Store } from '../../model/store';
import { Stores } from '../../model/stores';
import { StoresProductSummary } from '../../model/stores-product-summary';
import { SuccessResponse } from '../../services/shared/success-response';
import { StoresApiMapper } from './stores-api.mapper';
import { FindStoreCategoriesResponseDto } from './dtos/find-store-categories.response.dto';
import { FindStoreCategoriesQueryDto } from './dtos/find-store-categories.query.dto';
import { FindStoreCategoryProductsQueryDto } from './dtos/find-store-category-products.query.dto';
import { FindStoreCategoryProductsResponseDto } from './dtos/find-store-category-products.response.dto';
import { FindStorePosResponseDto } from './dtos/find-store-pos.response.dto';
import { FindStorePosProductsQueryDto } from './dtos/find-store-pos-products.query.dto';
import { FindStorePosQueryDto } from './dtos/find-store-pos.query.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportStoreCategoryProductsQueryDto } from './dtos/export-store-category-products.query.dto';
import { ExportStoreProductsQueryDto } from './dtos/export-store-products.query.dto';
import { FindStoreProductsQueryDto } from './dtos/find-store-products.query.dto';
import { FindStoreProductsResponseDto } from './dtos/find-store-products.response.dto';
import { CreateStoreRequestDto } from './dtos/create-store.request.dto';
import { ExportExcelPdfService } from '../../services/export-excel-pdf/export-excel-pdf.service';
import { CreateStoreResponseDto } from './dtos/create-store-response.dto';
import { UpdateStoreRequestDto } from './dtos/update-store.request.dto';
import { UpdateStoreResponseDto } from './dtos/update-store-response.dto';
import { FindStoreInvoiceMetadataResponseDto } from './dtos/find-store-invoice-metadata.response.dto';
import { FindStoreResponseDto } from './dtos/find-store.response.dto';
import { FindStoreBirComplianceResponseDto } from './dtos/find-store-bir-compliance.response.dto';
import { UpdateStoreAutoZReportRequestDto } from './dtos/update-store-auto-z-report.request.dto';
import { FindStoreNamesResponseDto } from './dtos/find-store-names.response.dto';

@Injectable({
  providedIn: 'root',
})
export class StoresApiService implements IStoresApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);
  private readonly API_URL = `${environment.api}/stores`;

  data = {
    stores: {
      items: [],
      queryOptions: { page: 1, size: 10 } as QueryOptions<IStore>,
      subject: new BehaviorSubject<Maybe<Store[]>>(null),
      totalSubject: new BehaviorSubject<Maybe<number>>(null),
      totalAfterFilterSubject: new BehaviorSubject<Maybe<number>>(null),
    },
  };

  get storesData() {
    return this.data['stores'] as Data<Store, IStore>;
  }

  async updateStoreAutoZReport(storeId: number, dto: UpdateStoreAutoZReportRequestDto) {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateStoreAutoZReportRequestDto>>(
        `${this.API_URL}/${storeId}/auto-z-report`,
        dto
      )
    );

    return response.data;
  }

  async findStoreCategories(
    storeId: number,
    query: FindStoreCategoriesQueryDto
  ): Promise<FindStoreCategoriesResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreCategoriesResponseDto>>(
        `${this.API_URL}/${storeId}/categories`,
        { params }
      )
    );

    return response.data;
  }

  async findStoreCategoryProducts(
    storeId: number,
    categoryId: number,
    query: FindStoreCategoryProductsQueryDto
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreCategoryProductsResponseDto>>(
        `${this.API_URL}/${storeId}/categories/${categoryId}/products`,
        { params }
      )
    );

    return response.data;
  }

  async findStoreProducts(storeId: number, query: FindStoreProductsQueryDto) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreProductsResponseDto>>(
        `${this.API_URL}/${storeId}/products`,
        { params }
      )
    );

    return response.data;
  }

  async exportStoreCategoryProducts(
    storeId: number,
    categoryId: number,
    query: ExportStoreCategoryProductsQueryDto,
    options?: HandleBlobHttpResponseOptions
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(
        `${this.API_URL}/${storeId}/categories/${categoryId}/products/export`,
        {
          responseType: 'blob',
          observe: 'response',
          params,
        }
      )
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async exportStoreProducts(
    storeId: number,
    query: ExportStoreProductsQueryDto,
    options?: HandleBlobHttpResponseOptions
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/${storeId}/products/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      })
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  async findStorePos(storeId: number, query: FindStorePosQueryDto) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStorePosResponseDto>>(
        `${this.API_URL}/${storeId}/pos`,
        { params }
      )
    );

    return response.data;
  }

  async findStorePosProducts(
    storeId: number,
    query: FindStorePosProductsQueryDto
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreCategoryProductsResponseDto>>(
        `${this.API_URL}/${storeId}/pos/products`,
        { params }
      )
    );

    return response.data;
  }

  async createStore(
    dto: CreateStoreRequestDto
  ): Promise<CreateStoreResponseDto> {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateStoreResponseDto>>(this.API_URL, dto)
    );

    return response.data;
  }

  getStores(
    size: number,
    page: number,
    searchValue?: string
  ): Observable<Stores> {
    let params = new HttpParams();
    const pageForSearch = searchValue && page !== 1 ? 1 : page;
    params = params.set('page', pageForSearch);
    params = params.set('size', size);

    if (searchValue) {
      params = params.set('searchValue', searchValue);
    }

    return this.http
      .get<SuccessResponse>(this.API_URL, { params })
      .pipe(map((response) => StoresApiMapper.toStores(response.data)));
  }

  async findStoreNames() {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreNamesResponseDto>>(`${this.API_URL}/names`),
    );

    return response.data;
  }

  getCategories(): Observable<ICategory[]> {
    return this.http.get<ICategory[]>(`${this.API_URL}/categories/`);
  }

  getProductById(productId: number): Observable<IProduct | undefined> {
    return this.http.get<IProduct>(`${this.API_URL}/products/${productId}`);
  }

  getStoresProductSummary(): Observable<StoresProductSummary> {
    return this.http
      .get<SuccessResponse>(`${this.API_URL}/product-summary`)
      .pipe(
        map((response) => {
          return StoresApiMapper.toStoresProductSummary(response.data);
        })
      );
  }

  getTotalItemCount(): Observable<number> {
    return this.http.get<number>(`${this.API_URL}/count`);
  }

  searchStores(searchTerm: string): Observable<Store[]> {
    return this.http.get<Store[]>(`${this.API_URL}?search=${searchTerm}`);
  }

  getStoreById(storeId: number): Observable<IStore | undefined> {
    return this.http.get<IStore>(`${this.API_URL}/${storeId}`);
  }

  async findStore(storeId: number): Promise<FindStoreResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreResponseDto>>(
        `${this.API_URL}/${storeId}`,
      )
    );

    return response.data
  }

  async findStoreBirCompliance(storeId: number): Promise<FindStoreBirComplianceResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreBirComplianceResponseDto>>(
        `${this.API_URL}/${storeId}/bir-compliance`,
      )
    );

    return response.data
  }

  async findStoreInvoiceMetadata(storeId: number): Promise<FindStoreInvoiceMetadataResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindStoreInvoiceMetadataResponseDto>>(
        `${this.API_URL}/${storeId}/invoice-metadata`,
      )
    );

    return response.data
  }

  async updateStore(
    storeId: number,
    dto: UpdateStoreRequestDto
  ): Promise<UpdateStoreResponseDto> {
    const response = await firstValueFrom(
      this.http.patch<SuccessResponse<UpdateStoreResponseDto>>(
        `${this.API_URL}/${storeId}`,
        dto
      )
    );

    return response.data;
  }

  deleteStores(storeIds: Store['id'][]): Observable<boolean> {
    return this.http.put<boolean>(`${this.API_URL}/${'delete-many'}`, {
      ids: storeIds,
    });
  }
}
